import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import CloseIcon from "assets/icons/close.svg?react";
import budgetService, { DEFAULT_BUDGET_YEAR } from "api/budgetService";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import ModalsManagerContext from "contexts/ModalsManager";
import { IBudgetExpense } from "dto/Budget/IBudgetExpense";
import { formatMoney } from "utils/formatMoney";
import { ENotificationType, notification } from "utils/notification";
import AmountField from "components/CreateRegistryEntryModal/AmountField";
import styles from "./styles.module.scss";

const todayISO = () => new Date().toISOString().slice(0, 10);
const endOfYearISO = (year: number) => `${year}-12-31`;

interface Props {
  initialExpenseId?: string;
}

const CreateBudgetEngagementSlideModal = ({ initialExpenseId }: Props) => {
  const { setModals, modals } = useContext(ModalsManagerContext);
  const refWrapper = useRef(null);

  const year = DEFAULT_BUDGET_YEAR;
  const [expense, setExpense] = useState<IBudgetExpense | null>(null);

  const [title, setTitle] = useState("");
  const [engagementDate, setEngagementDate] = useState(todayISO());
  const [executionDeadline, setExecutionDeadline] = useState(
    endOfYearISO(year)
  );
  const [grantingProcedure, setGrantingProcedure] = useState("");
  const [hasProcedureDescription, setHasProcedureDescription] = useState(false);
  const [procedureDescription, setProcedureDescription] = useState("");
  const [hasJustification, setHasJustification] = useState(false);
  const [justification, setJustification] = useState("");
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  const { data: expenses = [] } = budgetService.useGetExpenses(year);

  useEffect(() => {
    if (initialExpenseId && expenses.length > 0 && !expense) {
      const found = expenses.find((e) => e.Id === initialExpenseId) ?? null;
      if (found) handleChooseExpense(found);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialExpenseId, expenses]);
  const { data: paragraphs = [] } = budgetService.useGetParagraphs();
  const { data: procedures = [] } = budgetService.useGetGrantingProcedures();
  const { data: existingEngagements = [] } = budgetService.useGetEngagements(
    expense?.Id ?? "",
    !!expense
  );
  const { mutate: createEngagement, isLoading } =
    budgetService.useCreateEngagement();

  const handleCloseModal = () => {
    setModals({ ...modals, hide: true });
  };

  const paragraphLabel = (paragraphId: string, suffix: string) => {
    const p = paragraphs.find((x) => x.Id === paragraphId);
    return `${p?.Symbol ?? "?"}${suffix !== "0" ? suffix : ""} - ${
      p?.Name ?? ""
    }`;
  };

  const engagedByQuota = useMemo(() => {
    const map: Record<string, number> = {};
    existingEngagements
      .flatMap((e) => e.Quotas)
      .forEach((q) => {
        map[q.ExpenseQuotaId] = (map[q.ExpenseQuotaId] ?? 0) + q.Amount;
      });
    return map;
  }, [existingEngagements]);

  const freeByQuota = (quotaId: string, planned: number) =>
    planned - (engagedByQuota[quotaId] ?? 0);

  const total = useMemo(
    () =>
      Object.values(amounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0),
    [amounts]
  );

  const exceedsFree = !!expense?.Quotas.some((q) => {
    const entered = parseFloat(amounts[q.Id]) || 0;
    return entered > 0 && entered > freeByQuota(q.Id, q.Amount);
  });

  const canSubmit =
    !!expense &&
    !!title.trim() &&
    !!grantingProcedure.trim() &&
    total > 0 &&
    !exceedsFree;

  const handleChooseExpense = (next: IBudgetExpense | null) => {
    setExpense(next);
    setExecutionDeadline(endOfYearISO(next?.Year ?? year));
    setAmounts({});
  };

  const handleSubmit = () => {
    if (!canSubmit || !expense) return;
    createEngagement(
      {
        ExpenseId: expense.Id,
        Title: title.trim(),
        EngagementDate: engagementDate,
        ExecutionDeadline: executionDeadline,
        GrantingProcedure: grantingProcedure.trim(),
        GrantingProcedureDescription: hasProcedureDescription
          ? procedureDescription.trim()
          : undefined,
        Justification: hasJustification ? justification.trim() : undefined,
        Quotas: expense.Quotas.filter(
          (q) => (parseFloat(amounts[q.Id]) || 0) > 0
        ).map((q) => ({
          ExpenseQuotaId: q.Id,
          Amount: parseFloat(amounts[q.Id]),
        })),
      },
      {
        onSuccess: () => {
          handleCloseModal();
        },
        onError: () =>
          notification({
            title: "Wystąpił błąd",
            text: "Nie udało się utworzyć zaangażowania.",
            type: ENotificationType.ERROR,
          }),
      }
    );
  };

  return (
    <ModalLayout
      name="createBudgetEngagement"
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      ignore
      refWrapper={refWrapper}
      isAnimation
      fullHeight={true}
    >
      <>
        <div className={styles.wrapper}>
          <div className={styles.title}>
            Nowe zaangażowanie
            <CloseIcon
              className={styles.closeIcon}
              onClick={handleCloseModal}
            />
          </div>

          <div className={styles.content}>
            <Autocomplete
              options={expenses}
              value={expense}
              getOptionLabel={(o) => o.Title}
              isOptionEqualToValue={(a, b) => a.Id === b.Id}
              onChange={(_, next) => handleChooseExpense(next)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Wydatek budżetowy"
                  placeholder="Wybierz wydatek"
                />
              )}
            />

            {expense && (
              <>
                <TextField
                  label="Tytuł"
                  size="small"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />

                <div className={styles.dates}>
                  <TextField
                    label="Data zaangażowania"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={engagementDate}
                    onChange={(e) => setEngagementDate(e.target.value)}
                  />
                  <TextField
                    label="Termin realizacji"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={executionDeadline}
                    onChange={(e) => setExecutionDeadline(e.target.value)}
                  />
                </div>

                <Autocomplete
                  freeSolo
                  options={procedures}
                  inputValue={grantingProcedure}
                  onInputChange={(_, v) => setGrantingProcedure(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Tryb udzielania zamówienia publicznego"
                    />
                  )}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasProcedureDescription}
                      onChange={(e) =>
                        setHasProcedureDescription(e.target.checked)
                      }
                    />
                  }
                  label="Opis dodatkowy trybu udzielania zamówienia publicznego"
                />
                {hasProcedureDescription && (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={procedureDescription}
                    onChange={(e) => setProcedureDescription(e.target.value)}
                  />
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasJustification}
                      onChange={(e) => setHasJustification(e.target.checked)}
                    />
                  }
                  label="Uzasadnienie celowości dokonania wydatku"
                />
                {hasJustification && (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                  />
                )}

                <span className={styles.sectionTitle}>Paragrafy</span>
                {expense.Quotas.map((q) => {
                  const free = freeByQuota(q.Id, q.Amount);
                  const entered = parseFloat(amounts[q.Id]) || 0;
                  const over = entered > 0 && entered > free;
                  return (
                    <div className={styles.quota} key={q.Id}>
                      <div className={styles.quotaInfo}>
                        <span className={styles.quotaLabel}>
                          {paragraphLabel(q.ParagraphId, q.ParagraphSuffix)}
                        </span>
                        <span className={styles.free}>
                          Wolne środki {formatMoney(free)}
                        </span>
                      </div>
                      <AmountField
                        liveFormat
                        label="Kwota"
                        error={over}
                        helperText={
                          over ? "Kwota przekracza wolne środki" : undefined
                        }
                        value={amounts[q.Id] ?? ""}
                        onChange={(v) =>
                          setAmounts((prev) => ({ ...prev, [q.Id]: v }))
                        }
                      />
                    </div>
                  );
                })}

                <div className={styles.total}>
                  <span>Zaangażowane środki</span>
                  <strong>{formatMoney(total)}</strong>
                </div>
              </>
            )}
          </div>

          <div className={styles.footer}>
            <button
              className={styles.submit}
              disabled={!canSubmit || isLoading}
              onClick={handleSubmit}
            >
              Przekaż do akceptacji
            </button>
          </div>
        </div>
      </>
    </ModalLayout>
  );
};

export default CreateBudgetEngagementSlideModal;
